import { parseISO } from 'date-fns';
import { app } from 'zapatos/schema';
import { DateRange } from '../../types';
import { sum } from '../../util/arrays';
import { dt } from '../../util/dateTime';
import { typedKeys } from '../../util/objects';
import { makeTick, makeTickRange, Tick } from '../../valueSeries';
import { HISTORICAL_MONTHS } from './constants';
import { CashflowTransactionEntry } from './types';

type TickUnit = 'day' | 'month';
export type TransactionsChartType = 'line' | 'bar' | 'netCashflow';

export type CashflowAmount = {
    // The total amount for the current date
    current: number;
    // The 3-month average amount for the current date
    currentAvg: number;
    // The total amount for all dates up to and including the current date
    cumulative: number;
    // The 3-month average amount for all dates up to and including the current date
    cumulativeAvg: number;
};

export interface TransactionsChartDatum extends Tick {
    SPENDING: CashflowAmount;
    INCOME: CashflowAmount;
    SAVING_INVESTING: CashflowAmount;
    TRANSFERS: CashflowAmount;
}

export const getTransactionChartData = ({
    transactionEntries,
    historicalTransactionEntries,
    dateRange,
}: {
    transactionEntries: CashflowTransactionEntry[];
    historicalTransactionEntries: CashflowTransactionEntry[];
    dateRange: DateRange;
}) => {
    const unit =
        dt(dateRange.endDate).diff(dateRange.startDate, 'month') > 3
            ? 'month'
            : 'day';

    const transactionEntriesByDate = groupTransactionEntriesByDate(
        transactionEntries,
        unit
    );
    const historicalTransactionEntriesByDate = groupTransactionEntriesByDate(
        historicalTransactionEntries,
        unit
    );

    const amounts = {
        current: 0,
        currentAvg: 0,
        cumulative: 0,
        cumulativeAvg: 0,
    };

    const totals: Record<app.supercategory, CashflowAmount> = {
        SPENDING: { ...amounts },
        INCOME: { ...amounts },
        SAVING_INVESTING: { ...amounts },
        TRANSFERS: { ...amounts },
    };

    const values: TransactionsChartDatum[] = [];

    let date = dt(dateRange.startDate).startOf(unit).toDateStr();
    while (date <= dateRange.endDate) {
        // Reset current values for each date
        typedKeys(totals).forEach((supercategory) => {
            totals[supercategory].current = 0;
            totals[supercategory].currentAvg = 0;
        });

        const historicalAverages = calculateHistoricalAverages(
            historicalTransactionEntriesByDate,
            date
        );

        typedKeys(totals).forEach((supercategory) => {
            totals[supercategory].currentAvg =
                historicalAverages[supercategory].amount;
            totals[supercategory].cumulativeAvg +=
                historicalAverages[supercategory].amount;
        });

        for (const transactionEntry of transactionEntriesByDate[date] || []) {
            typedKeys(totals).forEach((supercategory) => {
                if (
                    transactionEntry.category.categoryGroup.supercategory ===
                    supercategory
                ) {
                    totals[supercategory].current += transactionEntry.amount;
                    totals[supercategory].cumulative += transactionEntry.amount;
                }
            });
        }

        values.push({
            ...makeTick(date, unit),
            SPENDING: { ...totals.SPENDING },
            INCOME: { ...totals.INCOME },
            SAVING_INVESTING: { ...totals.SAVING_INVESTING },
            TRANSFERS: { ...totals.TRANSFERS },
        });

        date = dt(date).add(1, unit).toDateStr();
    }

    const range = makeTickRange(dateRange.startDate, dateRange.endDate, unit);

    return {
        valueSeries: {
            ...range,
            startDateObj: parseISO(range.startDate),
            values,
        },
        range,
    };
};

// Get the three-month average of each supercategory's total on the same day/month
// For example, if `date` is 2024-01-01, then we want to get the average of spending on 2023-12-01, 2023-11-01, and 2023-10-01
const calculateHistoricalAverages = (
    historicalTransactionEntriesByDate: Record<
        string,
        CashflowTransactionEntry[]
    >,
    date: string
) => {
    const result: Record<app.supercategory, { amount: number }> = {
        SPENDING: { amount: 0 },
        INCOME: { amount: 0 },
        SAVING_INVESTING: { amount: 0 },
        TRANSFERS: { amount: 0 },
    };

    typedKeys(result).forEach((supercategory) => {
        const total = sum(
            Array.from({ length: HISTORICAL_MONTHS }, (_, i) => i + 1),
            (i) => {
                const historicalTransactionEntriesForDate =
                    historicalTransactionEntriesByDate[
                        dt(date).subtract(i, 'month').toDateStr()
                    ] || [];

                return sum(
                    historicalTransactionEntriesForDate.filter(
                        (t) =>
                            t.category.categoryGroup.supercategory ===
                            supercategory
                    ),
                    (t) => t.amount
                );
            }
        );

        result[supercategory].amount = Math.round(total / HISTORICAL_MONTHS);
    });

    return result;
};

const groupTransactionEntriesByDate = (
    transactionEntries: CashflowTransactionEntry[],
    unit: TickUnit
) => {
    const groupedTransactionEntries: Record<
        string,
        CashflowTransactionEntry[]
    > = {};

    for (const transactionEntry of transactionEntries) {
        if (transactionEntry.isHidden) continue;

        const date = dt(transactionEntry.date).startOf(unit).toDateStr();

        if (!groupedTransactionEntries[date]) {
            groupedTransactionEntries[date] = [];
        }

        groupedTransactionEntries[date].push(transactionEntry);
    }

    return groupedTransactionEntries;
};
